import React from "react";
import { createPortal } from "react-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ApiError } from "@atlas/shared";
import {
  Check,
  ChevronRight,
  Compass,
  Copy,
  Grid3x3,
  Landmark,
  Mountain,
  Plus,
  Route,
  Settings,
  UsersRound,
  X,
} from "lucide-react";
import {
  fetchGroup,
  fetchGroupBoard,
  fetchGroupCollections,
  fetchGroupFeed,
  fetchGroupRecent,
  type ChallengeSummary,
  type GroupBoardPeriod,
  type GroupBoardTab,
  type GroupCollectionSummary,
  type GroupDetailResponse,
  type GroupMember,
  type GroupRecentItem,
} from "../../api/groups";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ImageCredit } from "../../components/ImageCredit";
import { PostCard } from "../../components/PostCard";
import { Composer, UsernameClaimPrompt } from "../../components/PostComposer";
import { ProgressBar } from "../../components/ProgressBar";
import { Skel } from "../../components/Skeleton";
import { useAuth } from "../../hooks/useAuth";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePageMeta } from "../../hooks/usePageMeta";
import { CreateChallengeForm, ProChallengeNote } from "./CreateChallengeForm";
import { ordinal } from "./GroupsPage";
import {
  BOARD_UNITS,
  CARD_STYLE,
  CoverHero,
  LIFT_CSS,
  GroupsPageFrame,
  HERO_BACK_STYLE,
  HERO_CONTROL_STYLE,
  HERO_EYEBROW_STYLE,
  HERO_TEXT_SOFT,
  HERO_TITLE_STYLE,
  HeroBackLabel,
  MemberIdentity,
  MiniAvatar,
  SectionHeading,
  Segmented,
  Standings,
  WinnerMark,
  addDays,
  challengeUnit,
  challengeWhen,
  daysBetween,
  Dialog,
  fmtDay,
  fmtNumber,
  groupErrorMessage,
  londonToday,
  memberName,
  winnerNames,
} from "./group-ui";

// /groups/:id (spec 32 / ATL-151). A cover hero (group image, or the covers of
// the group's top collections), then two tabs: Standings (board, challenges,
// collection boards) and Updates (feed beside a recently-collected timeline).
// Strava rule: boards rank Atlas counts only, recent collections are item +
// day only.

const WIDE_QUERY = "(min-width: 1180px)";

// ─── Invite link ──────────────────────────────────────────────────────────────

export function inviteLink(code: string, username: string | null | undefined): string {
  const base = `${window.location.origin}/groups/join/${encodeURIComponent(code)}`;
  return username ? `${base}?ref=${encodeURIComponent(username)}` : base;
}

function CopyInviteButton({ code }: { code: string }) {
  const { user } = useAuth();
  const [copied, setCopied] = React.useState(false);
  const link = inviteLink(code, user?.username);

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this invite link", link);
    }
  }

  return (
    <button type="button" onClick={() => void copy()} title={link} style={HERO_CONTROL_STYLE}>
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Link copied" : "Copy invite link"}
    </button>
  );
}

// ─── Members pill + popover ───────────────────────────────────────────────────

function roleLabel(m: GroupMember): React.ReactNode {
  if (m.role === "member") return undefined;
  return <span className="eyebrow" style={{ fontSize: 9.5 }}>{m.role === "owner" ? "Owner" : "Admin"}</span>;
}

function MembersPill({ data }: { data: GroupDetailResponse }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<React.CSSProperties>({});
  const pillRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const count = data.group.memberCount;
  const stack = data.members.slice(0, 4);

  const place = React.useCallback(() => {
    const r = pillRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const top = r.bottom + 8;
    const maxHeight = Math.max(200, window.innerHeight - top - 16);
    if (vw < 560) {
      setPos({ top, left: 16, right: 16, maxHeight });
    } else {
      const width = 320;
      const left = Math.min(Math.max(16, r.right - width), vw - width - 16);
      setPos({ top, left, width, maxHeight });
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    place();
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (pillRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        pillRef.current?.focus();
      }
    }
    function onScroll(e: Event) {
      // The page scrolls an inner container; keep the panel only for its own list.
      if (panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={pillRef}
        type="button"
        data-members-pill
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ ...HERO_CONTROL_STYLE, height: 36, paddingLeft: 4, gap: 8 }}
      >
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {stack.map((m, i) => (
            <span key={m.userId} style={{ marginLeft: i === 0 ? 0 : -7, display: "inline-flex", position: "relative", zIndex: stack.length - i }}>
              <MiniAvatar member={m} size={28} ring="var(--ink-950)" />
            </span>
          ))}
        </span>
        <span className="tnum">
          {fmtNumber(count)} member{count === 1 ? "" : "s"}
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Group members"
            className="atlas-glass"
            style={{
              position: "fixed",
              zIndex: 1000,
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-lg)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              ...pos,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
              <span style={{ font: "var(--type-h3)", fontSize: 15, color: "var(--text-primary)" }}>
                Members <span className="tnum" style={{ color: "var(--text-muted)", fontWeight: 500 }}>· {fmtNumber(count)}</span>
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                style={{ background: "none", border: "none", padding: 4, cursor: "pointer", color: "var(--text-muted)", display: "inline-flex" }}
              >
                <X size={16} />
              </button>
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: "8px 14px 12px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
              {data.members.map((m) => (
                <li key={m.userId} style={{ minWidth: 0 }}>
                  <MemberIdentity member={m} subtitle={roleLabel(m)} />
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function GroupHero({ data, covers }: { data: GroupDetailResponse; covers: string[] }) {
  const { group } = data;
  const narrow = useMediaQuery("(max-width: 560px)");
  const images = group.imageUrl ? [group.imageUrl] : covers;
  return (
    <CoverHero
      images={images}
      top={
        <>
          <Link to="/groups" style={HERO_BACK_STYLE}>
            <HeroBackLabel label="All groups" />
          </Link>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", minWidth: 0 }}>
            <MembersPill data={data} />
            <CopyInviteButton code={group.inviteCode} />
            <Link
              to="/groups/$id/settings"
              params={{ id: group.id }}
              aria-label="Group settings"
              title="Group settings"
              style={{ ...HERO_CONTROL_STYLE, padding: narrow ? 0 : "0 12px", width: narrow ? 34 : undefined }}
            >
              <Settings size={14} /> {!narrow && "Settings"}
            </Link>
          </div>
        </>
      }
    >
      <p style={HERO_EYEBROW_STYLE}>Private group</p>
      <h1 style={HERO_TITLE_STYLE}>{group.name}</h1>
      {group.description && (
        <p
          style={{
            fontSize: 14.5,
            color: HERO_TEXT_SOFT,
            margin: 0,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            maxWidth: 620,
            textShadow: "0 1px 8px rgba(0,0,0,.35)",
          }}
        >
          {group.description}
        </p>
      )}
    </CoverHero>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────

type PageTab = "standings" | "updates";

function PageTabs({ value, onChange }: { value: PageTab; onChange: (t: PageTab) => void }) {
  const tabs: Array<{ value: PageTab; label: string }> = [
    { value: "standings", label: "Standings" },
    { value: "updates", label: "Updates" },
  ];
  return (
    <div role="tablist" aria-label="Group sections" style={{ display: "flex", gap: 22, borderBottom: "1px solid var(--border-subtle)", marginTop: -4 }}>
      {tabs.map((t) => {
        const active = t.value === value;
        return (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.value)}
            style={{
              background: "none",
              border: "none",
              padding: "10px 2px 11px",
              marginBottom: -1,
              borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
              color: active ? "var(--text-primary)" : "var(--text-muted)",
              fontFamily: "var(--font-sans)",
              fontSize: 14.5,
              fontWeight: active ? 650 : 500,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Board ────────────────────────────────────────────────────────────────────

const TAB_OPTIONS: Array<{ value: GroupBoardTab; label: string }> = [
  { value: "collected", label: "Collected" },
  { value: "score", label: "Outdoor Score" },
  { value: "new_ground", label: "New ground" },
];

const PERIOD_OPTIONS: Array<{ value: GroupBoardPeriod; label: string }> = [
  { value: "month", label: "This month" },
  { value: "all", label: "All time" },
];

const TAB_HINTS: Record<GroupBoardTab, string> = {
  collected: "Hills, landmarks and trail sections collected for the first time.",
  score: "Outdoor Score, all time.",
  new_ground: "New explored squares on the map.",
};

function BoardSection({ groupId }: { groupId: string }) {
  const [tab, setTab] = React.useState<GroupBoardTab>("collected");
  const [period, setPeriod] = React.useState<GroupBoardPeriod>("month");
  const effectivePeriod: GroupBoardPeriod = tab === "score" ? "all" : period;

  const boardQuery = useQuery({
    queryKey: ["groups", groupId, "board", tab, effectivePeriod],
    queryFn: () => fetchGroupBoard(groupId, tab, effectivePeriod),
  });

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <SectionHeading title="Board" hint={TAB_HINTS[tab]} />
      <div style={{ ...CARD_STYLE, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <Segmented label="Board" value={tab} options={TAB_OPTIONS} onChange={setTab} />
          {tab !== "score" && <Segmented label="Period" value={period} options={PERIOD_OPTIONS} onChange={setPeriod} />}
        </div>
        {boardQuery.isLoading ? (
          <>
            <Skel height="44px" />
            <Skel height="44px" />
            <Skel height="44px" />
          </>
        ) : boardQuery.isError ? (
          <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
            {groupErrorMessage(boardQuery.error, "Couldn't load the board.")}
          </p>
        ) : (
          <Standings rows={boardQuery.data?.rows ?? []} unit={BOARD_UNITS[tab]} emptyHint="Nobody is on the board yet." />
        )}
      </div>
    </section>
  );
}

// ─── Challenges ───────────────────────────────────────────────────────────────

function prizeLine(c: ChallengeSummary): string | null {
  if (c.prize && (c.prizeStatus === "approved" || c.prizeStatus === "granted")) {
    const what =
      c.prize.kind === "waypoints"
        ? `${fmtNumber(c.prize.amount ?? 0)} Waypoints`
        : (c.prize.itemName ?? "A store item");
    return c.prizeStatus === "granted" ? `Prize: ${what}, granted` : `Prize: ${what}, backed by Atlas`;
  }
  if (c.prizeStatus === "requested") return "Prize requested, waiting on Atlas";
  return null;
}

/** Target picture: the collection cover or region hero, else topo with an icon. */
function ChallengeThumb({ challenge }: { challenge: ChallengeSummary }) {
  const src = challenge.target.imageUrl;
  const Icon = challenge.target.type === "new_ground" ? Grid3x3 : Compass;
  return (
    <div
      aria-hidden="true"
      className={src ? undefined : "atlas-topo"}
      style={{
        width: 84,
        flexShrink: 0,
        alignSelf: "stretch",
        minHeight: 84,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRight: "1px solid var(--border-subtle)",
        color: "var(--text-muted)",
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Icon size={22} strokeWidth={1.8} />
      )}
    </div>
  );
}

/** Card-sized version of challengeWhen: one short clause. */
function shortWhen(c: ChallengeSummary): string {
  const today = londonToday();
  if (c.state === "upcoming") {
    const days = daysBetween(today, c.startsOn);
    return days <= 1 ? `Starts ${days === 0 ? "today" : "tomorrow"}` : `Starts ${fmtDay(c.startsOn)}`;
  }
  if (c.state === "active") {
    const left = daysBetween(today, c.endsOn);
    return left <= 0 ? "Last day" : `${left} day${left === 1 ? "" : "s"} left`;
  }
  if (c.state === "ended") return "Results in the morning";
  return `Finished ${fmtDay(c.endsOn)}`;
}

function ChallengeCard({ challenge }: { challenge: ChallengeSummary }) {
  const finished = challenge.state === "finalised";
  const unit = challengeUnit(challenge.target.type, challenge.countRepeats);
  const prize = prizeLine(challenge);
  return (
    <Link
      to="/groups/$id/challenges/$cid"
      params={{ id: challenge.groupId, cid: challenge.id }}
      className="grp-lift"
      style={{ ...CARD_STYLE, display: "flex", alignItems: "center", overflow: "hidden", textDecoration: "none", color: "inherit" }}
    >
      <ChallengeThumb challenge={challenge} />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4, padding: "11px 12px" }}>
        <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text-primary)", overflowWrap: "anywhere" }}>
          {challenge.title}
        </span>
        <span
          className="tnum"
          title={`${challenge.target.name} · ${challengeWhen(challenge)}`}
          style={{ fontSize: 12, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {challenge.target.name} · {shortWhen(challenge)}
        </span>
        {finished ? (
          challenge.winners.length > 0 ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-secondary)" }}>
              <WinnerMark size={13} />
              <span style={{ minWidth: 0 }}>
                {winnerNames(challenge)}
                <span className="tnum" style={{ color: "var(--text-muted)" }}>
                  {" · "}
                  {fmtNumber(challenge.winners[0]!.value)} {unit}
                </span>
              </span>
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Nobody scored.</span>
          )
        ) : challenge.me ? (
          <span className="tnum" style={{ fontSize: 12.5, color: "var(--text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            You: {challenge.me.rank != null ? ordinal(challenge.me.rank) : "not on the board yet"} · {fmtNumber(challenge.me.value)} {unit}
          </span>
        ) : null}
        {prize && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{prize}</span>}
      </div>
      <ChevronRight size={15} style={{ color: "var(--text-faint)", flexShrink: 0, marginRight: 12 }} />
    </Link>
  );
}

function ChallengesSection({ data }: { data: GroupDetailResponse }) {
  const [creating, setCreating] = React.useState(false);
  const isAdmin = data.me.role === "owner" || data.me.role === "admin";
  const live = data.challenges.filter((c) => c.state === "upcoming" || c.state === "active" || c.state === "ended");
  const past = data.challenges.filter((c) => c.state === "finalised");

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <SectionHeading
        title="Challenges"
        hint="Set dates, one target, most wins."
        action={
          isAdmin ? (
            <Button variant="secondary" size="sm" leftIcon={<Plus size={13} />} onClick={() => setCreating(true)}>
              New challenge
            </Button>
          ) : undefined
        }
      />
      {creating && (
        <Dialog title={data.me.isPro ? "New challenge" : "Challenges need Atlas Pro"} onClose={() => setCreating(false)}>
          {data.me.isPro ? (
            <CreateChallengeForm groupId={data.group.id} memberCount={data.group.memberCount} onCancel={() => setCreating(false)} />
          ) : (
            <ProChallengeNote />
          )}
        </Dialog>
      )}
      {live.length === 0 && past.length === 0 && (
        <div style={{ ...CARD_STYLE, padding: 16 }}>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
            {isAdmin
              ? "No challenges yet. Set one on a collection, a region, anything or new ground, with a start and an end."
              : "No challenges yet. A group admin can start one."}
          </p>
        </div>
      )}
      {live.map((c) => (
        <ChallengeCard key={c.id} challenge={c} />
      ))}
      {past.length > 0 && (
        <>
          <p className="eyebrow" style={{ fontSize: 10, margin: live.length > 0 ? "8px 0 0" : 0 }}>Finished</p>
          {past.map((c) => (
            <ChallengeCard key={c.id} challenge={c} />
          ))}
        </>
      )}
    </section>
  );
}

// ─── Collections (all time) ───────────────────────────────────────────────────

// Six fills two rows of three in the wide column; phones and mid widths show eight.
const COLLECTIONS_SHOWN_WIDE = 6;
const COLLECTIONS_SHOWN = 8;

export const COLLECTION_COUNT_RULE =
  "All time. A hill or landmark counts once collected, a trail once every section is done.";

function collectionsQueryOptions(groupId: string) {
  return {
    queryKey: ["groups", groupId, "collections"],
    queryFn: () => fetchGroupCollections(groupId),
  };
}

function CollectionTile({ groupId, item, compact }: { groupId: string; item: GroupCollectionSummary; compact: boolean }) {
  const others = item.leader ? item.leader.tied - 1 : 0;
  return (
    <Link
      to="/groups/$id/collections/$slug"
      params={{ id: groupId, slug: item.slug }}
      className="grp-lift"
      style={{ ...CARD_STYLE, display: "flex", flexDirection: "column", overflow: "hidden", textDecoration: "none", color: "inherit" }}
    >
      <div
        className={item.coverImage ? undefined : "atlas-topo"}
        style={{ position: "relative", height: compact ? 84 : 116, flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-faint)" }}
      >
        {item.coverImage ? (
          <>
            <img src={item.coverImage} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
            <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(9,12,16,0) 50%, rgba(9,12,16,.45) 100%)" }} />
            <ImageCredit src={item.coverImage} placement="corner" />
          </>
        ) : (
          <Mountain size={24} strokeWidth={1.6} aria-hidden="true" />
        )}
      </div>
      <div style={{ padding: compact ? "10px 11px 12px" : "12px 14px 14px", display: "flex", flexDirection: "column", gap: compact ? 7 : 9, minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            fontSize: compact ? 14.5 : 16,
            color: "var(--text-primary)",
            letterSpacing: "-.01em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.name}
        </span>
        {item.leader ? (
          <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, fontSize: 12.5, color: "var(--text-secondary)" }}>
            <MiniAvatar member={item.leader.user} size={20} />
            <span className="tnum" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {memberName(item.leader.user)}
              {others > 0 && ` and ${others} other${others === 1 ? "" : "s"}`}
              <span style={{ color: "var(--text-muted)" }}> · {fmtNumber(item.leader.value)}</span>
            </span>
          </span>
        ) : (
          <span style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: "20px" }}>Nobody has any yet</span>
        )}
        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          <ProgressBar value={item.me.value} max={Math.max(1, item.total)} size="sm" color="sky" showValue={false} />
          <span className="tnum" style={{ fontSize: 12, color: item.me.value > 0 ? "var(--text-secondary)" : "var(--text-muted)" }}>
            {item.me.value > 0
              ? `You: ${fmtNumber(item.me.value)} / ${fmtNumber(item.total)}${item.me.rank != null ? ` · ${ordinal(item.me.rank)}` : ""}`
              : `You: none yet · ${fmtNumber(item.total)} to collect`}
          </span>
        </div>
      </div>
    </Link>
  );
}

function CollectionsSection({ groupId }: { groupId: string }) {
  const [showAll, setShowAll] = React.useState(false);
  const collectionsQuery = useQuery(collectionsQueryOptions(groupId));
  // Two to a row on phones, so eight cards aren't eight screens of scrolling.
  const compact = useMediaQuery("(max-width: 560px)");
  const wide = useMediaQuery(WIDE_QUERY);
  const limit = wide ? COLLECTIONS_SHOWN_WIDE : COLLECTIONS_SHOWN;
  const grid: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: compact ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fill, minmax(220px, 1fr))",
    gap: compact ? 10 : 14,
    minWidth: 0,
  };
  const items = collectionsQuery.data?.items ?? [];
  const anyProgress = items.some((i) => i.membersWithProgress > 0);
  const shown = showAll ? items : items.slice(0, limit);
  const toggle =
    anyProgress && items.length > limit ? (
      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--text-accent)", fontSize: 13, fontWeight: 600 }}
      >
        {showAll ? "Show fewer" : `Show all ${fmtNumber(items.length)}`}
      </button>
    ) : undefined;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
      <SectionHeading title="Collections" hint={`Who has the most of each list. ${COLLECTION_COUNT_RULE}`} action={toggle} />
      {collectionsQuery.isLoading ? (
        <div style={grid}>
          {Array.from({ length: 4 }, (_, i) => (
            <Skel key={i} height="236px" radius="var(--radius-lg)" />
          ))}
        </div>
      ) : collectionsQuery.isError ? (
        <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
          {groupErrorMessage(collectionsQuery.error, "Couldn't load the collections.")}
        </p>
      ) : !anyProgress ? (
        <div style={{ ...CARD_STYLE, padding: 16 }}>
          <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
            Nobody in the group has anything from a collection yet.
          </p>
        </div>
      ) : (
        <div style={grid}>
          {shown.map((item) => (
            <CollectionTile key={item.slug} groupId={groupId} item={item} compact={compact} />
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Recently collected (timeline) ────────────────────────────────────────────

const ITEM_ICONS = {
  peak: Mountain,
  landmark: Landmark,
  trail_section: Route,
} as const;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Today", "Yesterday", or "Sat 10 Oct". Split, never parsed as a Date. */
function dayHeading(ymd: string): string {
  const today = londonToday();
  if (ymd === today) return "Today";
  if (ymd === addDays(today, -1)) return "Yesterday";
  const [y, m, d] = ymd.split("-").map((n) => Number(n));
  const weekday = WEEKDAYS[new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()] ?? "";
  return `${weekday} ${fmtDay(ymd)}`;
}

function RecentEntry({ item }: { item: GroupRecentItem }) {
  const Icon = ITEM_ICONS[item.itemType];
  const linkStyle: React.CSSProperties = {
    color: "var(--text-primary)",
    fontWeight: 600,
    textDecoration: "none",
    overflowWrap: "anywhere",
  };
  const name =
    item.itemType === "peak" ? (
      <Link to="/peaks/$slug" params={{ slug: item.slug }} style={linkStyle}>{item.name}</Link>
    ) : item.itemType === "landmark" ? (
      <Link to="/landmarks/$slug" params={{ slug: item.slug }} style={linkStyle}>{item.name}</Link>
    ) : (
      <Link to="/trails/$id" params={{ id: item.slug }} style={linkStyle}>{item.name}</Link>
    );
  return (
    <li style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
      <MiniAvatar member={item.user} size={26} />
      <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.45, paddingTop: 3 }}>
        {memberName(item.user)} collected {name}
      </span>
      <Icon size={14} style={{ color: "var(--text-faint)", flexShrink: 0, marginTop: 5 }} aria-label={item.itemType === "trail_section" ? "Trail section" : item.itemType === "peak" ? "Hill" : "Landmark"} />
    </li>
  );
}

function RecentTimeline({ groupId, initial }: { groupId: string; initial: number }) {
  const [showAll, setShowAll] = React.useState(false);
  const recentQuery = useQuery({
    queryKey: ["groups", groupId, "recent"],
    queryFn: () => fetchGroupRecent(groupId),
  });
  const items = recentQuery.data?.items ?? [];
  const shown = showAll ? items : items.slice(0, initial);
  const days: Array<{ day: string; entries: GroupRecentItem[] }> = [];
  for (const item of shown) {
    const last = days[days.length - 1];
    if (last && last.day === item.day) last.entries.push(item);
    else days.push({ day: item.day, entries: [item] });
  }

  return (
    <section style={{ ...CARD_STYLE, padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h2 style={{ font: "var(--type-h3)", fontSize: 16, color: "var(--text-primary)", margin: 0 }}>Recently collected</h2>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "2px 0 0" }}>First visits in the last 30 days.</p>
      </div>
      {recentQuery.isLoading ? (
        <Skel height="140px" />
      ) : recentQuery.isError ? (
        <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
          {groupErrorMessage(recentQuery.error, "Couldn't load recent collections.")}
        </p>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>Nothing new in the last 30 days.</p>
      ) : (
        <>
          {days.map((group) => (
            <div key={group.day} style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
              <p className="eyebrow tnum" style={{ fontSize: 9.5, margin: 0 }}>{dayHeading(group.day)}</p>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                {group.entries.map((item, i) => (
                  <RecentEntry key={`${item.user.userId}-${item.itemType}-${item.slug}-${i}`} item={item} />
                ))}
              </ul>
            </div>
          ))}
          {items.length > initial && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              style={{ alignSelf: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--text-accent)", fontSize: 13, fontWeight: 600 }}
            >
              {showAll ? "Show less" : `Show more (${fmtNumber(items.length - initial)})`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ─── Feed ─────────────────────────────────────────────────────────────────────

function GroupFeed({ groupId, groupName }: { groupId: string; groupName: string }) {
  const { user } = useAuth();
  // Under the "social/feed" prefix so the composer's and PostCard's existing
  // invalidations refresh it too.
  const feedQuery = useInfiniteQuery({
    queryKey: ["social/feed", "group", groupId],
    queryFn: ({ pageParam }) => fetchGroupFeed(groupId, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
  const posts = feedQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
      <SectionHeading title="Feed" hint="Posts here are seen by group members only. Members' public posts show up here too." />
      {user && !user.username ? (
        <UsernameClaimPrompt />
      ) : (
        <Composer attach={null} groupId={groupId} placeholder={`Post to ${groupName}…`} />
      )}
      {feedQuery.isLoading ? (
        <>
          <Skel height="160px" radius="var(--radius-lg)" />
          <Skel height="160px" radius="var(--radius-lg)" />
        </>
      ) : feedQuery.isError ? (
        <EmptyState
          icon={<UsersRound size={20} />}
          title="Couldn't load the feed"
          hint={groupErrorMessage(feedQuery.error, "Try again in a moment.")}
        />
      ) : posts.length === 0 ? (
        <EmptyState icon={<UsersRound size={20} />} title="Nothing posted yet" hint="Start it off: a walk, a summit, a plan for the weekend." />
      ) : (
        posts.map((post) => <PostCard key={post.id} post={post} />)
      )}
      {feedQuery.hasNextPage && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button variant="secondary" onClick={() => void feedQuery.fetchNextPage()} disabled={feedQuery.isFetchingNextPage}>
            {feedQuery.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function GroupNotFound({ error }: { error: unknown }) {
  const notFound = error instanceof ApiError && error.status === 404;
  return (
    <GroupsPageFrame>
      <EmptyState
        icon={<UsersRound size={20} />}
        title={notFound ? "This group isn't available" : "Couldn't load this group"}
        hint={
          notFound
            ? "You may have left it, or it was deleted. Ask a member for an invite link to get back in."
            : groupErrorMessage(error, "Try again in a moment.")
        }
      />
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Link to="/groups" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-accent)" }}>
          Back to your groups
        </Link>
      </div>
    </GroupsPageFrame>
  );
}

export default function GroupPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const search = useSearch({ strict: false }) as { tab?: PageTab };
  const navigate = useNavigate();
  const tab: PageTab = search.tab === "updates" ? "updates" : "standings";
  const wide = useMediaQuery(WIDE_QUERY);

  const groupQuery = useQuery({
    queryKey: ["groups", id],
    queryFn: () => fetchGroup(id),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  // Same key as the Collections section, so the hero mosaic costs no extra request.
  const collectionsQuery = useQuery({ ...collectionsQueryOptions(id), enabled: groupQuery.isSuccess });

  usePageMeta({
    title: "Group - Atlas",
    description: "A private Atlas group.",
    canonicalPath: "/groups",
    noindex: true,
  });

  function setTab(next: PageTab) {
    void navigate({
      to: "/groups/$id",
      params: { id },
      search: next === "updates" ? { tab: "updates" } : {},
      replace: true,
    });
  }

  if (groupQuery.isLoading) {
    return (
      <GroupsPageFrame wide>
        <Skel height="240px" radius="var(--radius-xl)" />
        <Skel height="36px" width="220px" />
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 24 }}>
          <Skel height="320px" radius="var(--radius-lg)" />
          <Skel height="320px" radius="var(--radius-lg)" />
        </div>
      </GroupsPageFrame>
    );
  }
  if (groupQuery.isError || !groupQuery.data) return <GroupNotFound error={groupQuery.error} />;

  const data = groupQuery.data;
  const covers = (collectionsQuery.data?.items ?? [])
    .map((i) => i.coverImage)
    .filter((src): src is string => !!src)
    .slice(0, 3);
  const column: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 28, minWidth: 0 };

  return (
    <GroupsPageFrame wide>
      <style>{LIFT_CSS}</style>
      <GroupHero data={data} covers={covers} />
      <PageTabs value={tab} onChange={setTab} />

      {tab === "standings" ? (
        <div style={column}>
          {wide ? (
            // Board then collections down the wide column, challenges beside
            // them, so neither column leaves a hole under a short neighbour.
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.75fr) minmax(0, 1fr)", gap: 28, alignItems: "start" }}>
              <div style={column}>
                <BoardSection groupId={id} />
                <CollectionsSection groupId={id} />
              </div>
              <ChallengesSection data={data} />
            </div>
          ) : (
            <>
              <BoardSection groupId={id} />
              <ChallengesSection data={data} />
              <CollectionsSection groupId={id} />
            </>
          )}
        </div>
      ) : wide ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)", gap: 28, alignItems: "start" }}>
          <GroupFeed groupId={id} groupName={data.group.name} />
          <div style={{ position: "sticky", top: 0, minWidth: 0 }}>
            <RecentTimeline groupId={id} initial={14} />
          </div>
        </div>
      ) : (
        <div style={column}>
          <RecentTimeline groupId={id} initial={5} />
          <GroupFeed groupId={id} groupName={data.group.name} />
        </div>
      )}
    </GroupsPageFrame>
  );
}
